import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const PLANS = ['trial', 'starter', 'pro'];
const STATUSES = ['trial', 'active', 'expired', 'frozen'];

const MODEL_OPTIONS = [
  { value: '',                          label: 'Otomatik (Plana Göre)' },
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
  { value: 'claude-sonnet-4-5-20251001',label: 'Claude Sonnet 4.5' },
  { value: 'claude-opus-4-5-20251001',  label: 'Claude Opus 4.5' },
  { value: 'gpt-4o',                   label: 'GPT-4o' },
  { value: 'gpt-4o-mini',              label: 'GPT-4o Mini' },
];

function MiniBarChart({ data }) {
  if (!data || data.length === 0) return <div style={{ color: '#94a3b8', fontSize: 13 }}>Veri yok</div>;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80, paddingTop: 8 }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
          <div
            title={`${d.date}: ${d.count} mesaj`}
            style={{
              width: '100%', minWidth: 4,
              height: `${Math.max(4, (d.count / max) * 64)}px`,
              background: 'linear-gradient(180deg, #6366f1, #3b82f6)',
              borderRadius: '3px 3px 0 0',
            }}
          />
        </div>
      ))}
    </div>
  );
}

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState('');
  const [status, setStatus] = useState('');
  const [assignedModel, setAssignedModel] = useState('');
  const [savingModel, setSavingModel] = useState(false);
  const [adminNote, setAdminNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [actionLogs, setActionLogs] = useState([]);

  useEffect(() => {
    api.get(`/admin/customers/${id}`)
      .then(d => {
        setData(d);
        setPlan(d.hotel?.plan || 'trial');
        setStatus(d.hotel?.status || 'trial');
        setAssignedModel(d.hotel?.assignedModel || '');
        setAdminNote(d.hotel?.adminNote || '');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    api.get(`/admin/customers/${id}/logs`).then(d => setActionLogs(d.logs || [])).catch(() => {});
  }, [id]);

  const handleSavePlan = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/customers/${id}/plan`, { plan, status });
      toast.success('Plan güncellendi');
      setData(prev => ({ ...prev, hotel: { ...prev.hotel, plan, status } }));
    } catch (e) {
      toast.error('Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const extendTrial = async () => {
    const newEnd = new Date(data.hotel.trialEndsAt || Date.now());
    newEnd.setDate(newEnd.getDate() + 7);
    setSaving(true);
    try {
      await api.patch(`/admin/customers/${id}/plan`, { status: 'trial' });
      toast.success('Deneme 7 gün uzatıldı');
    } catch (e) {
      toast.error('Uzatma başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      await api.patch(`/admin/customers/${id}/note`, { adminNote });
      toast.success('Not kaydedildi');
      const logs = await api.get(`/admin/customers/${id}/logs`);
      setActionLogs(logs.logs || []);
    } catch (e) {
      toast.error('Not kaydedilemedi');
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveModel = async () => {
    setSavingModel(true);
    try {
      await api.patch(`/admin/customers/${id}/model`, { assignedModel: assignedModel || null });
      toast.success('Model güncellendi');
      setData(prev => ({ ...prev, hotel: { ...prev.hotel, assignedModel: assignedModel || null } }));
    } catch (e) {
      toast.error('Model güncellenemedi');
    } finally {
      setSavingModel(false);
    }
  };

  const toggleFreeze = async () => {
    const newStatus = data.hotel?.status === 'frozen' ? 'active' : 'frozen';
    setSaving(true);
    try {
      await api.patch(`/admin/customers/${id}/plan`, { status: newStatus });
      setData(prev => ({ ...prev, hotel: { ...prev.hotel, status: newStatus } }));
      setStatus(newStatus);
      toast.success(newStatus === 'frozen' ? 'Hesap donduruldu' : 'Hesap aktifleştirildi');
    } catch (e) {
      toast.error('İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, color: '#64748b' }}>Yükleniyor...</div>;
  if (!data) return <div style={{ padding: 40, color: '#ef4444' }}>Otel bulunamadı.</div>;

  const hotel = data.hotel;

  return (
    <div style={{ padding: 32 }}>
      {/* Back button */}
      <button
        onClick={() => navigate('/admin/customers')}
        style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 16, padding: 0 }}
      >
        ← Müşteriler
      </button>

      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>{hotel.name}</h1>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', color: '#64748b', fontSize: 14, marginBottom: 28 }}>
        {hotel.email && <span>📧 {hotel.email}</span>}
        {hotel.phone && <span>📞 {hotel.phone}</span>}
        {hotel.city && <span>📍 {hotel.city}</span>}
        {hotel.address && <span>🏠 {hotel.address}</span>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Stats */}
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>Genel İstatistikler</h3>
          <div style={{ display: 'flex', gap: 24 }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#1e293b' }}>{data.stats.totalMessages}</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>Toplam Mesaj</div>
            </div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#1e293b' }}>{data.stats.totalReservations}</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>Toplam Rezervasyon</div>
            </div>
          </div>
        </div>

        {/* Plan Management */}
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>Plan Yönetimi</h3>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <select
              value={plan}
              onChange={e => setPlan(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, background: 'white' }}
            >
              {PLANS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, background: 'white' }}
            >
              {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
            <button
              onClick={handleSavePlan}
              disabled={saving}
              style={{ padding: '8px 16px', borderRadius: 8, background: '#6366f1', color: 'white', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
            >
              Kaydet
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={extendTrial}
              disabled={saving}
              style={{ padding: '7px 14px', borderRadius: 8, background: '#fef3c7', color: '#92400e', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              Denemeyi 7 Gün Uzat
            </button>
            <button
              onClick={toggleFreeze}
              disabled={saving}
              style={{
                padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: hotel.status === 'frozen' ? '#dcfce7' : '#fee2e2',
                color: hotel.status === 'frozen' ? '#166534' : '#991b1b',
              }}
            >
              {hotel.status === 'frozen' ? 'Aktifleştir' : 'Hesabı Dondur'}
            </button>
          </div>
        </div>
      </div>

      {/* AI Model Assignment */}
      <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 24 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>AI Model Ataması</h3>
        <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 14 }}>
          Varsayılan: plana göre otomatik. Manuel atama plan modelini ezer.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={assignedModel}
            onChange={e => setAssignedModel(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, background: 'white', minWidth: 220 }}
          >
            {MODEL_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={handleSaveModel}
            disabled={savingModel}
            style={{ padding: '8px 16px', borderRadius: 8, background: '#6366f1', color: 'white', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
          >
            {savingModel ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
          {hotel.assignedModel && (
            <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>
              Manuel atama aktif: {MODEL_OPTIONS.find(o => o.value === hotel.assignedModel)?.label || hotel.assignedModel}
            </span>
          )}
        </div>
      </div>

      {/* Message Chart */}
      <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Son 30 Gün — Günlük Mesaj</h3>
        <MiniBarChart data={data.last30DaysMessages} />
      </div>

      {/* Reservations */}
      <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>Son Rezervasyonlar</h3>
        {data.reservations.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>Henüz rezervasyon yok.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Müşteri', 'Giriş', 'Çıkış', 'Durum', 'Tarih'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.reservations.map(r => (
                <tr key={r.id}>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontWeight: 600, color: '#1e293b' }}>{r.customerName}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>{new Date(r.checkIn).toLocaleDateString('tr-TR')}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>{new Date(r.checkOut).toLocaleDateString('tr-TR')}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: r.status === 'CONFIRMED' ? '#10b981' : r.status === 'REJECTED' ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>
                    {r.status}
                  </td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#94a3b8' }}>{new Date(r.createdAt).toLocaleDateString('tr-TR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Admin Notu */}
      <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginTop: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Admin Notu</h3>
        <textarea
          value={adminNote}
          onChange={e => setAdminNote(e.target.value)}
          placeholder="Bu müşteri hakkında dahili not ekleyin (yalnızca admin görür)..."
          rows={4}
          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, resize: 'vertical', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
        />
        <button
          onClick={handleSaveNote}
          disabled={savingNote}
          style={{ marginTop: 10, padding: '8px 20px', borderRadius: 8, background: '#6366f1', color: 'white', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
        >
          {savingNote ? 'Kaydediliyor...' : 'Notu Kaydet'}
        </button>
      </div>

      {/* Aksiyon Geçmişi */}
      {actionLogs.length > 0 && (
        <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginTop: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>Admin Aksiyon Geçmişi</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {actionLogs.map(log => (
              <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: '#6366f1', minWidth: 120 }}>{log.action}</span>
                {log.detail && <span style={{ color: '#475569', fontFamily: 'monospace', fontSize: 12 }}>{log.detail}</span>}
                <span style={{ marginLeft: 'auto', color: '#94a3b8', whiteSpace: 'nowrap', fontSize: 12 }}>
                  {new Date(log.createdAt).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
